// pages/Profile.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { apiFetch } from "../api";
import { useAuth } from "../context/AuthContext";
import { getAvatarUrl } from "../constants/avatars";
import "../styles/pages/_profile.css";
import { timeAgo, formatDate, formatDateTime, TimeAgo } from "../helpers/time";

// Activity Item
const ROLE_LABELS = { user: "User", moderator: "Mod", admin: "Admin" };

//cut the long text
const MAX_EXCERPT = 100;
function truncate(text, max = MAX_EXCERPT) {
    if (!text) return text;
    return text.length > max ? text.slice(0, max) + "…" : text;
}

function ActivityItem({ item }) {
    if (item.type === "thread") {
        return (
            <li className="prof-activity-item prof-activity-item--thread">
                <span className="prof-activity-icon" title="New thread">
                    <span className="material-symbols-outlined">apps</span>
                </span>
                <div className="prof-activity-body">
                    <div className="prof-activity-header">
                        <span className="prof-activity-label">Started a thread</span>
                        <span className="prof-activity-time">{timeAgo(item.created_at)}</span>
                    </div>
                    <Link to={`/thread/${item.id}`} className="prof-activity-title" title={item.title}>
                        {item.title}
                    </Link>
                    <span className="prof-activity-cat">
                        in <Link to={`/forum/${item.category_id}`}>{item.category_name}</Link>
                    </span>
                    {item.excerpt && (
                        <p className="prof-activity-excerpt">{truncate(item.excerpt)}</p>
                    )}
                </div>
            </li>
        );
    }

    return (
        <li className="prof-activity-item prof-activity-item--post">
            <span className="prof-activity-icon" title="Reply">
                <span className="material-symbols-outlined">chat</span>
            </span>
            <div className="prof-activity-body">
                <div className="prof-activity-header">
                    <span className="prof-activity-label">Replied in</span>
                    <span className="prof-activity-time">{timeAgo(item.created_at)}</span>
                </div>
                <Link to={`/thread/${item.thread_id}`} className="prof-activity-title" title={item.thread_title}>
                    {item.thread_title}
                </Link>
                <span className="prof-activity-cat">
                    in <Link to={`/forum/${item.category_id}`}>{item.category_name}</Link>
                </span>
                {item.excerpt && (
                    <p className="prof-activity-excerpt">{truncate(item.excerpt)}</p>
                )}
            </div>
        </li>
    );
}

// Main Component

export default function Profile() {
    const { accountId } = useParams();
    const { user: currentUser } = useAuth();

    const [profile, setProfile]     = useState(null);
    const [activity, setActivity]   = useState([]);
    const [actPage, setActPage]     = useState(1);
    const [actPages, setActPages]   = useState(1);
    const [loading, setLoading]     = useState(true);
    const [actLoading, setActLoading] = useState(false);
    const [error, setError]         = useState("");

    // Load profile
    useEffect(() => {
        setLoading(true);
        setError("");
        setActivity([]);
        setActPage(1);

        apiFetch(`/api/profiles/${accountId}`)
            .then((data) => setProfile(data.profile))
            .catch((err) => setError(err.message || "Failed to load profile"))
            .finally(() => setLoading(false));
    }, [accountId]);

    // Load activity
    const loadActivity = useCallback(
        async (page = 1) => {
            setActLoading(true);
            try {
                const data = await apiFetch(
                    `/api/profiles/${accountId}/activity?page=${page}&limit=15`
                );
                setActivity(data.activity);
                setActPage(data.pagination.page);
                setActPages(data.pagination.pages);
            } catch {
                // Silently fail — activity is supplementary
            } finally {
                setActLoading(false);
            }
        },
        [accountId]
    );

    useEffect(() => {
        if (profile) loadActivity(1);
    }, [profile, loadActivity]);

    // RENDER

    if (loading) {
        return <div className="prof-loading">Loading profile…</div>;
    }
    if (error) {
        return <div className="prof-error">{error}</div>;
    }
    if (!profile) {
        return <div className="prof-error">User not found.</div>;
    }

    const isOwnProfile = currentUser && currentUser.id === profile.id;
    const displayName  = profile.display_name || profile.username;

    return (
        <div className="wrapper prof-root">
            {/* Hero Card  */}
            <section className="prof-hero">
                <div className="prof-hero-bg" />
                <div className="prof-hero-content">
                    <img
                        className="prof-avatar"
                        src={getAvatarUrl(profile.avatar_url)}
                        alt={`${displayName}'s avatar`}
                    />
                    <div className="prof-hero-info">
                        <h1 className="prof-name">{displayName}</h1>
                        <span className="prof-username">@{profile.username}</span>
                        {profile.title && (
                            <span className="prof-title">{profile.title}</span>
                        )}
                        <span className={`role role--${profile.role}`}>
                            {ROLE_LABELS[profile.role] ?? profile.role}
                        </span>
                    </div>
                    {isOwnProfile && (
                        <Link to="/profile/edit" className="btn btn--danger">
                            Edit Profile
                        </Link>
                    )}
                </div>
            </section>

            {/* Stats Bar */}
            <section className="prof-stats">
                <div className="prof-stat">
                    <span className="prof-stat-value">{profile.post_count}</span>
                    <span className="prof-stat-label">Posts</span>
                </div>
                <div className="prof-stat">
                    <span className="prof-stat-value">{profile.thread_count}</span>
                    <span className="prof-stat-label">Threads</span>
                </div>
                <div className="prof-stat">
                    <span className="prof-stat-value">{formatDate(profile.created_at)}</span>
                    <span className="prof-stat-label">Member Since</span>
                </div>
            </section>

            {/* Body: Bio + Activity */}
            <div className="prof-body">
                {/* Bio Sidebar */}
                <aside className="prof-sidebar">
                    <div className="prof-bio-card">
                        <h2 className="prof-section-title">About</h2>
                        <p className="prof-bio-text">
                            {profile.bio || <em>This user hasn't written a bio yet.</em>}
                        </p>
                    </div>

                    {profile.signature && (
                        <div className="prof-sig-card">
                            <h2 className="prof-section-title">Signature</h2>
                            <p className="prof-sig-text">{profile.signature}</p>
                        </div>
                    )}
                </aside>

                {/* Activity Feed */}
                <section className="prof-activity">
                    <h2 className="prof-section-title">Recent Activity</h2>

                    {actLoading && activity.length === 0 ? (
                        <p className="prof-activity-loading">Loading activity…</p>
                    ) : activity.length === 0 ? (
                        <p className="prof-activity-empty">No activity yet.</p>
                    ) : (
                        <>
                            <ul className="prof-activity-list">
                                {activity.map((item, i) => (
                                    <ActivityItem key={`${item.type}-${item.id}-${i}`} item={item} />
                                ))}
                            </ul>

                            {actPages > 1 && (
                                <div className="prof-pagination">
                                    <button
                                        className="prof-page-btn"
                                        disabled={actPage <= 1 || actLoading}
                                        onClick={() => loadActivity(actPage - 1)}
                                    >
                                        Newer
                                    </button>
                                    <span className="prof-page-info">
                                        Page {actPage} of {actPages}
                                    </span>
                                    <button
                                        className="prof-page-btn"
                                        disabled={actPage >= actPages || actLoading}
                                        onClick={() => loadActivity(actPage + 1)}
                                    >
                                        Older
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}